/**
 * **고객 가져오기 화면의 문구 한 곳** — 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지 — 단위 시험이 node 에서 그대로
 * 읽는다).
 *
 * 서버(`/telInfo/import/**`, appserver WP-AP4)는 어휘(`to`·`how`·`why`·`checks`·`notes`·`reason`·`act` …)만 내리고 한국어
 * 문장은 화면이 만든다 — `callpopText.js`·`deliveryMenuSync.js` 와 같은 결이다. 서버의 `message` 는 기록·대비용이고 화면은
 * `reason` 으로 문장을 고른다(계약 I1). 그래서 **어휘 → 문장 표와 화면 문장이 전부 여기 있다** — 판정·조립은
 * [customerImport.js](./customerImport.js) 가 갖고, 그 파일에는 문장이 없다.
 *
 * 문장의 정본은 설계 시안(`docs/planning/pos2lite-phone-order-20261002/design.src.html` 의 관리자 웹 화면)이고, 바꾼 것은 둘이다:
 * **이름 뒤에 받침에 따라 갈리는 조사를 붙이지 않는다**(리포 관행 — 매장·칸 이름은 사장님이 정한 값이라 받침을 알 수 없다:
 * 「가온반점 누리점이 쓰던」 → 「이 매장(가온반점 누리점)이 쓰던」, 「1마일리지가 1원인가요?」 → 「‘마일리지’ 칸의 1이 1원인가요?」),
 * 그리고 시안의 흉내(단계 체크 애니메이션)를 실제 진행 문장으로 바꿨다. 받침과 무관한 조사(「에」·「의」)는 쓴다.
 *
 * 계약 정본: `ordern-workspace/docs/planning/pos2lite-phone-order-impl-20261002/wp-ap4-customer-import-brief.md` §1-0 I1~I11
 * (준비 문서 §3-1 이 덮은 것 포함) · 화면 브리프 `wp-w1-admin-web-brief.md` §1-3.
 */
// 확장자를 적는 이유: 단위 시험이 앱 번들 밖(node)에서 이 파일을 그대로 읽는다 — node ESM 은 확장자를 추론하지 않는다
import { numberText } from './formatterNum.js'

/** 날짜·시각 표기(`kstDate.formatKst` 형식) — 같은 파일 배너의 날, 가져온 기록의 때, 진행의 시각 */
export const IMPORT_DATE_FORMAT = Object.freeze({ DAY: 'M월 d일', DAY_TIME: 'M월 d일 HH:mm', TIME: 'HH:mm' })

/** 포인트 — `1,200P` */
export function pointText(value) {
  return `${numberText(value)}P`
}

/** 오더엔 칸(I10 `to`) — 칸 확인의 고르기 상자. 순서는 시안 `IMPORT_TARGETS` 그대로 */
export const TARGET_LABEL = Object.freeze({
  name: '이름',
  tel: '전화번호',
  addr: '주소',
  detail: '상세 주소',
  addr2: '두 번째 주소',
  memo: '메모·취향',
  grade: '메모 앞에 붙이기',
  points: '포인트 잔액',
  priorOrders: '이전 주문 수',
  priorAmount: '이전 누적 금액',
  priorLast: '이전 마지막 주문일',
  skip: '가져오지 않음',
})

/** 정한 방법(I10 `how`) — `type` 은 `el-tag` 의 종류 */
export const HOW_TAG = Object.freeze({
  dict: Object.freeze({ text: '흔한 칸 이름', type: 'info' }),
  check: Object.freeze({ text: '뜻 확인', type: 'warning' }),
  unknown: Object.freeze({ text: '모르는 칸', type: 'danger' }),
  user: Object.freeze({ text: '고침', type: 'success' }),
})

/** 사전이 그 칸을 그렇게 맞춘 까닭(I10 `why`) */
export const WHY_TEXT = Object.freeze({
  MANAGEMENT_NO: '이전 프로그램의 관리 번호',
  DETAIL_OR_SECOND: '보통 동·호·층을 적는 칸이에요 — 두 번째 주소면 바꿔 주세요',
  SECOND_ADDRESS: '배달 주소를 하나 더 적는 칸으로 봤어요',
  GRADE_PREFIX: '등급 칸이라 메모 앞에 붙여요',
  BALANCE_ASKED: '잔액인지는 미리보기에서 한 번 더 물어요',
  ACCUMULATED: '누적 적립이라 옮기지 않아요 — 지금 쓸 수 있는 잔액이면 ‘포인트 잔액’으로',
  PRIOR_AMOUNT: '지금까지 주문한 금액으로 봤어요',
  MARKETING: '마케팅 수신 동의는 옮기지 않아요',
  UNUSED: '전화주문에 쓰지 않는 칸이에요',
  NOT_IN_DICTIONARY: '사전에 없는 칸이에요 — 필요하면 골라 주세요',
})

/** 읽을 때 버린 칸의 종류(I10 `dropped.reason`) */
export const DROPPED_TEXT = Object.freeze({
  CARD: '카드번호',
  RRN: '주민번호',
  ACCOUNT: '계좌번호',
})

/** 매장이 볼 것(I10 `checks`) — 「확인할 것」 탭에 든다 */
export const CHECK_TEXT = Object.freeze({
  ADDRESS_NAME_ONLY: '건물 이름만 맞았어요 — 첫 전화 때 POS에서 건물 찾기',
  ADDRESS_NOT_FOUND: '건물을 못 찾아 글자 그대로 넣어요',
  MULTI_TEL: '한 줄에 번호가 여러 개라 번호마다 고객을 만들어요 — 유선이 사무실 대표번호일 수 있어요',
  POINT_CONFLICT: '포인트 값이 줄마다 달라요 — 하나를 골라 주세요',
  POINT_BAD: '포인트가 숫자가 아니거나 1억을 넘어 옮기지 않아요',
  MEMO_CUT: '메모가 255자를 넘어 뒷부분을 옮기지 못해요',
})

/** 알림만(I10 `notes`) */
export const NOTE_TEXT = Object.freeze({
  TEL_RESTORED: '번호 앞 0을 되살렸어요',
  OLD_MOBILE: '옛 01X 번호예요',
  SAFE_REVIVED: '050 번호를 살렸어요',
  DOOR_DROPPED: '공동현관 번호는 옮기지 않아요',
  SENSITIVE_DROPPED: '메모 속 민감한 번호는 옮기지 않아요',
  NAME_CUT: '이름이 30자를 넘어 잘라요',
  PREF_TAGS_CUT: '취향이 길어 뒤 항목은 옮기지 못해요',
  RIDER_MEMO_CUT: '라이더 요청이 길어 뒤 항목은 옮기지 못해요',
  PRIOR_DATE_UNREADABLE: '이전 마지막 주문일을 읽지 못했어요',
})

/** 제외 줄의 까닭(I10 제외 줄 `reason`) */
export const EXCLUDE_TEXT = Object.freeze({
  NO_TEL: '번호 없음',
  LAST4: '뒷 4자리만 있어 전화와 이을 수 없어요',
  SAFE: '050 안심·가상번호 — 배달앱을 거친 일회용일 수 있어요',
  REPRESENTATIVE: '대표번호',
  INVALID: '걸 수 없는 번호',
  USER_SKIPPED: '그 줄의 고객을 모두 뺌',
})

/** 고객 한 명의 포인트 결과(I10 `points.act`) — 시안 `POINT_TAG`. `type` 은 글자 색(`warn` 만 경고) */
export const POINT_TAG = Object.freeze({
  NONE: Object.freeze({ text: '', type: 'info' }),
  NOMOBILE: Object.freeze({ text: '못 옮김 · 휴대폰 번호 없음', type: 'warn' }),
  PENDING: Object.freeze({ text: '값 고르기', type: 'warn' }),
  ASK: Object.freeze({ text: '질문에 답하면 정해져요', type: 'info' }),
  OFF: Object.freeze({ text: '옮기지 않음', type: 'info' }),
  NEW: Object.freeze({ text: '새 회원', type: 'ok' }),
  ADD: Object.freeze({ text: '지금 잔액에 더함', type: 'ok' }),
  SKIP: Object.freeze({ text: '이미 회원 · 건너뜀', type: 'info' }),
  ALREADY: Object.freeze({ text: '이미 옮긴 번호', type: 'info' }),
})

/** 포인트 값을 못 읽은 까닭(Person `points.bad`) */
export const POINT_BAD_TEXT = Object.freeze({
  NOT_A_NUMBER: '숫자가 아니에요',
  OVER_LIMIT: '1억을 넘어요',
})

/** 가져오기를 누를 때 남은 일(`preview.block`) — 바닥 줄에 적는다. 값을 고를 고객은 이름이 들어 [IMPORT_SENTENCE] 에 있다 */
export const BLOCK_TEXT = Object.freeze({
  NO_PEOPLE: '가져올 고객이 없어요',
  POINT_QUESTIONS: '포인트 질문 두 개에 답해 주세요',
  POINT_CONFLICTS: '포인트 값을 골라야 할 고객이 있어요 — 고객 표에서 골라 주세요',
})

/**
 * 거절(I10 거절 `reason`, 400·409) → 점주에게 할 말. `null` 은 「문장 없이 다시 읽는다」(진행 중이라 읽으면 답이 보인다).
 * 갈래가 있는 것(형식 힌트·파일 형식·매장 없음)은 [REJECTION_VARIANT_TEXT] 가 덮는다.
 */
export const REJECTION_TEXT = Object.freeze({
  INVALID_REQUEST: '다시 올려 주세요',
  NOT_ATTESTED: '다시 올려 주세요',
  FILE_MISSING: '빈 파일이에요',
  FILE_TOO_LARGE: '한 번에 20MB까지 올릴 수 있어요 — 파일을 나눠 올려 주세요',
  UNSUPPORTED_FORMAT: '엑셀(xls·xlsx)이나 CSV 파일만 올릴 수 있어요',
  OLD_EXCEL: '이 파일은 열 수 없어요 — 엑셀에서 열어 ‘다른 이름으로 저장 → Excel 통합 문서’로 저장해 다시 올려 주세요',
  ENCRYPTED: '암호가 걸린 파일이에요 — 엑셀에서 암호를 풀고 저장해 다시 올려 주세요',
  UNREADABLE: '파일이 깨져 읽을 수 없어요 — 쓰시던 프로그램에서 다시 받아 올려 주세요',
  NO_ROWS: '고객 줄이 없어요 — 전체 고객을 내보냈는지 확인해 주세요',
  TOO_MANY_ROWS: '한 번에 5만 줄까지 올릴 수 있어요 — 파일을 나눠 올려 주세요',
  TOO_MANY_COLUMNS: '칸이 200개를 넘어요 — 고객 칸만 남겨 다시 올려 주세요',
  NO_TEL_COLUMN: '전화번호 칸을 골라 주세요',
  NOT_READY: null,
  IMPORTING: null,
  CLOSED: '이 가져오기는 끝났거나 그만뒀어요',
  ADD_NOT_ALLOWED: '이 가져오기에서는 ‘더하기’를 고를 수 없어요',
  SAME_FILE_NO_ADD: '이 가져오기에서는 ‘더하기’를 고를 수 없어요',
  PLAN_CHANGED: '다른 창에서 고친 내용이 있어 미리보기를 다시 불러왔어요',
  NO_PEOPLE: BLOCK_TEXT.NO_PEOPLE,
  POINT_QUESTIONS: BLOCK_TEXT.POINT_QUESTIONS,
  POINT_CONFLICTS: BLOCK_TEXT.POINT_CONFLICTS,
  STORE_BUSY: '이 매장에 다른 가져오기가 진행 중이에요 — 끝난 뒤 다시 눌러 주세요',
})

/** 거절의 갈래 문장 — 같은 `reason` 이라도 세부(`hint`·`format`·`fields`)가 고칠 방법을 바꾼다 */
export const REJECTION_VARIANT_TEXT = Object.freeze({
  /** `UNSUPPORTED_FORMAT` + `hint: SPREADSHEET_XML` */
  SPREADSHEET_XML: '‘XML 스프레드시트’로 저장된 파일이에요 — 엑셀에서 열어 ‘Excel 통합 문서’로 저장해 다시 올려 주세요',
  /** `FILE_TOO_LARGE` + `format: XLS|HTML` — 옛 엑셀·웹 표는 5MB 상한이다 */
  OLD_FORMAT_TOO_LARGE:
    '옛 엑셀(xls)·웹 표 파일은 5MB까지예요 — 엑셀에서 ‘Excel 통합 문서’로 저장하면 20MB까지 올릴 수 있어요',
  /** `INVALID_REQUEST` 의 `fields` 에 `storeCd` — 사업장 계정이 매장을 고르지 않았다 */
  STORE_REQUIRED: '매장을 먼저 골라 주세요',
  /** 404 — 작업 하나(올린 계정만 연다, I1) */
  JOB_NOT_FOUND: '이 가져오기는 열 수 없어요(올린 계정만 열 수 있어요)',
  /** 404 — 올리기(고른 매장이 범위 밖·없음) */
  STORE_NOT_FOUND: '고른 매장에 가져올 수 없어요',
  /** 올리기의 전송 실패 — 300초(로드밸런서 한도)를 넘겼을 수 있다. 서버는 끝냈을 수 있어 기록으로 안내한다 */
  UPLOAD_TRANSPORT:
    '올리는 데 오래 걸려 연결이 끊겼어요 — ‘가져온 기록’에 이 파일이 있으면 이어서 하고, 없으면 파일을 나눠 다시 올려 주세요',
  /** 이름이 없는 실패(5xx 등) */
  UNKNOWN: '요청을 처리하지 못했어요 — 잠시 뒤 다시 해 주세요',
})

/** 가져온 기록의 상태(I2 `status`) — `DONE` 은 말하지 않는다 */
export const HISTORY_STATUS_TEXT = Object.freeze({
  READING: '이어서 하기',
  MAPPING: '이어서 하기',
  REVIEW: '이어서 하기',
  IMPORTING: '가져오는 중',
  FAILED: '정리하지 못함',
})

/** 화면 문장(값이 들지 않는 것) */
export const IMPORT_TEXT = Object.freeze({
  steps: Object.freeze(['파일 올리기', '칸 확인', '미리보기', '끝']),
  changeStore: '매장 바꾸기',

  pickStoreTitle: '어느 매장의 고객인가요?',
  pickStoreLead:
    '사장님이 고객 목록을 맡긴 매장을 고르세요. 고른 매장과 올린 계정·시각이 그 매장 화면의 ‘가져온 기록’에도 보여요.',
  pickStorePlaceholder: '매장을 고르세요',

  pickTitle: '쓰시던 프로그램의 고객 목록을 올려 주세요',
  pickLines: Object.freeze([
    '엑셀(xls·xlsx)이나 CSV 파일을 받아요. 한 번에 5만 줄(20MB)까지 올릴 수 있어요.',
    '검색 결과만이 아니라 전체 고객을 내보냈는지 확인해 주세요.',
    '가져와도 고객에게 알림은 가지 않아요. 포인트는 휴대폰 번호 고객의 잔액만 옮겨요.',
  ]),
  howTitle: '쓰시던 프로그램에서 고객 목록 받는 법',
  howLines: Object.freeze([
    '고객 관리 화면의 ‘엑셀로 저장’이나 ‘내보내기’에 있어요. 셀을 고른 채로 저장하면 고른 칸만 저장되는 프로그램이 있으니, 아무 칸도 고르지 않고 저장해 주세요.',
    '메뉴를 못 찾으면 그 프로그램 고객센터에 ‘고객 목록 엑셀’을 요청하세요. 어려우면 담당 대리점에 맡길 수 있어요.',
    '열리지 않을 때 — 옛 엑셀 형식이면 엑셀에서 열어 ‘다른 이름으로 저장 → Excel 통합 문서’로, 암호가 걸려 있으면 암호를 풀고 저장해 다시 올려 주세요. 전화번호 칸을 못 찾으면 다음 단계에서 고르게 해 드려요.',
  ]),
  dropText: '파일을 여기에 끌어 놓거나',
  dropPick: '파일 고르기',
  dropHint: '엑셀(xls·xlsx)·CSV · 20MB까지',

  readingTitle: '파일을 읽고 있어요',
  uploadingNote: '올리는 동안에는 창을 닫지 마세요.',
  cleaningTitle: '정리하고 있어요',
  importingTitle: '고객을 가져오고 있어요',
  pointsMovingTitle: '포인트를 옮기고 있어요',
  workingNote: '창을 닫아도 서버가 계속 정리하고, 다시 열면 이어서 보여 드려요.',
  stoppedPlain: '확인을 멈췄어요',
  recheck: '다시 확인',

  mappingTitle: '칸 확인',
  mappingSub: '흔한 칸 이름으로 맞췄어요',
  noTelColumn: '전화번호 칸을 찾지 못했어요 — 어느 칸인지 골라 주세요',
  mappingHeadFile: '파일 칸',
  mappingHeadExample: '파일 값 예',
  mappingHeadTarget: '오더엔 칸',
  mappingHeadHow: '정한 방법',
  foldRest: '나머지 칸 접기',
  otherFile: '다른 파일',
  submitMapping: '이대로 정리하기',
  backToReview: '미리보기로',

  checklistTitle: '확인할 것',
  checklistSub: '이 세 가지가 맞으면 가져와도 돼요',
  checkCountLabel: '고객 수',
  checkPointsLabel: '포인트',
  remap: '칸 다시 보기',
  pointsNoColumn: '포인트 칸이 없어 포인트는 옮기지 않아요.',
  pointsOff: '포인트는 옮기지 않기로 했어요.',

  pointsTitle: '포인트',
  pointsQuestion: '손님이 지금 바로 쓸 수 있는 금액인가요?',
  pointsAnswerBalance: '예, 지금 쓸 수 있는 잔액이에요',
  pointsAnswerIgnore: '아니요, 누적 적립이에요 — 옮기지 않기',
  unitAnswerWon: '예',
  unitAnswerSkip: '아니요 — 이번에는 옮기지 않기',
  existingSkip: '건너뛰기',
  existingAgency: '지금 잔액은 매장 계정에서만 보여요. 건너뛰어요.',
  existingSameFile: '같은 파일이라 다시 옮기지 않아요.',
  addWarning: '더하기는 그 회원으로 진행 중인 결제를 한 번 막을 수 있어요 — 영업이 끝난 뒤를 권해요.',
  memberFactsUnknown: '포인트 회원인지 지금 확인하지 못했어요 — 가져올 때 다시 확인해요.',
  /**
   * 포인트 카드 바닥 안내 — 점주 화면이라 개발 이름(프레임워크·플랫폼)을 쓰지 않는다. 어디서 쓰는지는 「지금 쓰는 POS」로 말하고,
   * 제품 이름은 아직 결제 화면이 열리지 않은 POS2 Lite 하나만 든다(10-03 메인 결정 — 시안 문장의 「Flutter·안드로이드 POS」를 바꿈)
   */
  pointsNote:
    '오더엔은 1P가 1원이고 소멸이 없어요(소멸 예정이던 포인트도 사라지지 않아요). 스탬프·선불권은 옮기지 않아요. 가져온 잔액은 따로 기록하고 적립 알림은 보내지 않아요. 가져온 포인트는 지금 쓰는 POS의 포인트 결제에서 바로 쓸 수 있어요. POS2 Lite에서는 결제 화면이 열린 뒤부터 써요.',

  peopleSub: '메모는 전화가 올 때 POS 화면에 보여요',
  peopleHeadTel: '번호',
  peopleHeadName: '이름',
  peopleHeadAddress: '주소 → 오더엔',
  peopleHeadMemo: '메모 · 취향 · 라이더',
  peopleHeadPoints: '포인트',
  peopleHeadResult: '결과',
  noName: '(이름 없음)',
  checkTabEmpty: '확인할 고객이 없어요.',
  tabEmpty: '없어요.',
  skip: '빼기',
  unskip: '다시 넣기',
  repick: '다시 고르기',
  newCustomer: '새 고객',
  mergedCustomer: '기존 고객과 합침',
  addressNotFound: '건물 확인 필요 · 글자 그대로',
  excludedHeadRow: '줄',
  excludedHeadName: '이름',
  excludedHeadTel: '번호',
  excludedHeadReason: '뺀 까닭',
  revive: '가져오기',
  unrevive: '다시 빼기',
  revived: '050 번호를 살려 가져와요',

  doneNote:
    '다음 전화부터 이 고객들을 알아봐요. 잘못 들어간 고객은 ‘주문 고객’에서 고치고, 포인트는 POS 포인트 조정으로 바로잡아요.',
  doneNoteAgency: '매장 화면의 ‘가져온 기록’에도 보여요.',
  doneFindHint: '가져온 고객은 ‘주문 고객’에서 번호·이름으로 찾아요.',
  goTelInfo: '주문 고객으로',
  goHistory: '가져온 기록',
  otherFileAgain: '다른 파일 올리기',
  retry: '다시 시도',

  historyTitle: '가져온 기록',
  historySub: '누가·언제·몇 명을 가져왔는지 남아요',
  historyHeadAt: '가져온 때',
  historyHeadStore: '매장',
  historyHeadActor: '올린 계정',
  historyHeadPeople: '고객',
  historyHeadPoints: '포인트',
  historyHeadStatus: '상태',
  storeActor: '매장 계정',
  agencyActorFallback: '사업장 계정',
  historyLoadFailed: '가져온 기록을 읽지 못했어요.',
  reload: '다시 읽기',
  none: '—',
})

/** 화면 문장(값이 드는 것) — 숫자는 [numberText]·[pointText], 이름 뒤에는 받침에 따라 갈리는 조사를 붙이지 않는다 */
export const IMPORT_SENTENCE = Object.freeze({
  /** 확인 줄 — 매장 계정(파일 고르는 칸 바로 위, 결정 17·review-U2 M6) */
  attestStore: (storeNm) =>
    `이 매장${
      storeNm ? `(${storeNm})` : ''
    }이 쓰던 프로그램의 고객 목록만 올려 주세요. 올리면 그렇게 확인한 것으로 기록해요.`,
  /** 확인 줄 — 사업장(대리점) 계정 */
  attestAgency: (storeNm) =>
    `${
      storeNm ? `${storeNm} ` : '고른 매장 '
    }사장님이 맡긴 고객 목록만 올려 주세요. 올린 계정과 시각을 매장에도 보여 드려요.`,
  uploadPercent: (percent) => `파일을 올리고 있어요 · ${percent}%`,
  progressCount: (done, total, unit) => `${numberText(done)}/${numberText(total)}${unit || ''}`,
  peopleUnit: '명',
  stalled: (time) => `잠시 멈췄어요 — 서버가 곧 이어서 처리해요(마지막 진행 ${time})`,
  stopped: (time) => `확인을 멈췄어요 · 마지막 진행 ${time}`,
  failed: (ref) => `정리하지 못했어요 · 오류 번호 ${ref == null ? '' : ref}`,

  fileLine: (name, rows) => `${name || ''} · ${numberText(rows)}줄`,
  ignoredLines: (count) => `제목·합계 줄 ${numberText(count)}개는 건너뛰었어요`,
  mappingLead: (checkCount, unknownCount) =>
    `뜻이 갈리는 칸 ${checkCount}개와 사전에 없는 칸 ${unknownCount}개를 확인해 주세요. 바꾸면 그 칸의 값으로 다시 정리해요.`,
  unfoldRest: (count) => `나머지 ${count}칸은 흔한 칸 이름으로 맞췄어요 · 보기`,
  droppedLine: (header, kind) => `${header} 칸은 읽을 때 버려서 저장하지 않아요${kind ? `(${kind})` : ''}.`,

  sameFile: (dateText) =>
    `${dateText}에 가져온 파일과 같아요. 새로 생긴 고객·주소만 더하고, 이미 옮긴 포인트는 다시 옮기지 않아요.`,
  countLine: (read, people, excluded, skipped) =>
    `파일 ${numberText(read)}줄 → 고객 ${numberText(people)}명(제외 ${numberText(excluded)}줄${
      skipped ? ` · 뺀 고객 ${numberText(skipped)}명` : ''
    }) — 쓰시던 프로그램의 고객 수와 같나요?`,
  checkedColumns: (count) => `확인한 칸 ${count}개`,
  columnPair: (header, label) => `${header} → ${label}`,
  pointsHeadline: (column, withPts, maxPts) =>
    `‘${column}’ 칸 · 0이 아닌 고객 ${numberText(withPts)}명 · 가장 큰 값 ${pointText(maxPts)}`,
  pointsAsk: (headline) => `${headline} — 아래 두 질문에 답해 주세요.`,
  pointsMoving: (count, sum, pending, notMoved) =>
    `옮김 ${numberText(count)}명 ${pointText(sum)}${
      pending ? ` · 값을 고를 고객 ${numberText(pending)}명` : ''
    } · 못 옮김 ${numberText(notMoved)}명`,

  /** 「1마일리지가 1원인가요?」의 조사 없는 꼴 */
  unitQuestion: (column) => `‘${column}’ 칸의 1이 1원인가요?`,
  newMembers: (members, newSum) => `새 포인트 회원 ${numberText(members)}명 · ${pointText(newSum)}`,
  memberValue: (who, value) => `${who} ${pointText(value)}`,
  more: (count) => ` 외 ${numberText(count)}명`,
  existingLead: (count) => `이미 포인트 회원 ${numberText(count)}명 — `,
  existingItem: (who, exPoints, value) => `${who} 지금 ${pointText(exPoints)}, 파일 ${pointText(value)}`,
  addChoice: (afters) => `지금 잔액에 더하기 → ${afters}`,
  already: (count) => `지난 가져오기에서 이미 옮긴 번호 ${numberText(count)}명 — 다시 옮기지 않아요.`,
  pending: (count) => `값이 줄마다 다른 고객 ${numberText(count)}명 — 아래 고객 표에서 골라 주세요.`,
  notMoved: (count, sum) =>
    count
      ? `못 옮김 ${numberText(count)}명 · ${pointText(sum)} (휴대폰 번호 없음·숫자가 아님·1억 초과)`
      : `못 옮김 ${numberText(count)}명`,
  memberCount: (before, after) => `가져오면 포인트 회원 ${numberText(before)}명 → ${numberText(after)}명`,

  peopleTitle: (count) => `고객 ${numberText(count)}명`,
  tabCheck: (count) => `확인할 것 ${numberText(count)}`,
  tabAll: (count) => `전체 ${numberText(count)}`,
  tabOut: (count) => `제외 ${numberText(count)}줄`,
  tabSkip: (count) => `뺀 고객 ${numberText(count)}`,
  fileRows: (rows) => `파일 ${(rows || []).join('·')}줄`,
  exName: (name) => `오더엔: ${name}`,
  pref: (tag) => `★${tag}`,
  rider: (list) => `라이더: ${list.join(' · ')}`,
  addPoints: (exPoints, after) => `지금 ${pointText(exPoints)} → ${pointText(after)}`,
  addressNameOnly: (name) => `건물 이름 ‘${name}’만 맞았어요 — 글자 그대로 넣고, 첫 전화 때 POS에서 건물을 찾아요`,
  pointConflicts: (names) => `${names} 고객의 포인트 값을 골라 주세요`,

  footSummary: (created, merged) => `새 고객 ${numberText(created)}명 · 기존 고객과 합침 ${numberText(merged)}명`,
  applyLabel: (storeNm, people) => `${storeNm}에 고객 ${numberText(people)}명 가져오기`,

  doneTitle: (storeNm, people) => `${storeNm}에 고객 ${numberText(people)}명을 가져왔어요`,
  doneSummary: (created, merged, members, pointsSum, excluded, skipped) =>
    `새 고객 ${numberText(created)}명 · 기존 고객과 합침 ${numberText(merged)}명 · 포인트 회원 ${numberText(
      members
    )}명 ${pointText(pointsSum)} · 제외 ${numberText(excluded)}줄${
      skipped ? ` · 뺀 고객 ${numberText(skipped)}명` : ''
    }`,
  doneSkippedMembers: (count) => `이미 포인트 회원이라 건너뜀 ${numberText(count)}명`,
  doneAlready: (count) => `이미 옮긴 번호 ${numberText(count)}명`,
  doneNotMoved: (count) => `포인트를 못 옮긴 고객 ${numberText(count)}명`,
  doneMemoCut: (count) => `메모가 잘린 고객 ${numberText(count)}명`,
  pointsPending: (count) => `포인트 ${numberText(count)}명 옮기지 못했어요`,

  historyCount: (count) => `${numberText(count)}명`,
  historyPoints: (members, pointsSum, pending) =>
    `${numberText(members)}명 ${pointText(pointsSum)}${pending ? ` · 포인트 대기 ${numberText(pending)}명` : ''}`,
})
